import styled from '@emotion/styled';
import { SmallText, H3, Text } from 'ds/components/typography';
import { LocalWinnersSection as BaseLocalWinnersSection } from 'components/madad/local-winners/styled';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';

export const SectionWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(8)};
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    margin-bottom: ${theme.spacing(7)};
  `}
`;

export const EducationsRoot = styled.div<{ withBottomPadding: boolean }>(({ theme, withBottomPadding }) => ({
  padding: theme.spacing(3, 3, withBottomPadding ? 3 : 0),
  borderRadius: theme.borderRadius.small,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
}));

export const ExtraInfoRoot = styled.div(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2, 7),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 3),
  borderRadius: theme.borderRadius.small,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  background: theme.colors.neutrals.white,
}));

export const ExtraInfoItem = styled.div<{ isHighlighted?: boolean }>(({ theme, isHighlighted }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.media.createObjectMediaQuery({ at: 1 })]: isHighlighted ? {
    flex: '1 1 100%',
  } : {},
}));

export const ExtraInfoLabel = styled(SmallText)<{ isHighlighted?: boolean }>(({ theme, isHighlighted }) => ({
  color: isHighlighted ? theme.colors.insights.education : theme.colors.neutrals.grey3,
}));

export const ExtraInfoVal = styled(Text)<{ isHighlighted?: boolean }>(({ theme, isHighlighted }) => ({
  color: isHighlighted ? theme.colors.insights.education : theme.colors.neutrals.grey1,
}));

export const EducationsSchoolTable = styled.table(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
  textAlign: 'right',
  width: '100%',
  'thead th': {
    paddingBottom: theme.spacing(2),
  },
  'thead tr': {
    borderBottom: `1px solid ${theme.colors.neutrals.grey8}`,
  },
  'tbody tr': {
    borderBottom: `1px dashed ${theme.colors.neutrals.grey8}`,
  },
  'tbody td': {
    padding: theme.spacing(2, 0),
  },
  'tbody td a': {
    color: theme.colors.brand.main,
  },
}));

export const TableHeader = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
  margin: theme.spacing(4, 0, 2),
}));

export const LocalRealtyWrapper = styled.div`
  ${({ theme }) => `
    margin: ${theme.spacing(8)} 0;
  `}
`;

export const OverviewWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const SectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    margin-bottom: ${theme.spacing(2)};
  `}
`;

export const SocialRankingSectionWrapper = styled.div`
  ${({ theme }) => `
    padding-top: ${theme.spacing(5)};
  `}
`;

export const TrendsInnerTitle = styled(H3)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const TrendsChartTitle = styled.div`
  height: 1px;
`;

export const ListingHeader = styled(SectionHeader)`
  display: flex;
  justify-content: space-between;


  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    flex-direction: column;
  `}
`;

export const SectionSubtitle = styled(SmallText)`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.colors.neutrals.grey3};
`;

export const KindergartensWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

export const KindergartenContainer = styled.li`
  flex-shrink: 0;
  width: 31%;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const RatingsContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-wrap: wrap;
  > div:first-of-type {
    margin: 0;
    margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ theme }) => theme.spacing(1.5)};
  }
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    > div:first-of-type {
      flex-basis: 100%;
      margin-bottom: ${theme.spacing(1)};
    }
  `};
`;

export const SingleRatingContainer = styled(SmallText)`
  display: flex;
  align-items: center;
  margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ theme }) => theme.spacing(1.5)};
  svg {
    margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ theme }) => theme.spacing(1)};
    path {
      fill: ${({ theme }) => theme.colors.neutrals.grey1};
    }
  }
`;

export const UserRatingsContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(5)} ${theme.spacing(2)}`};
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    padding: ${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(1)};
  `}
  border-radius: ${({ theme }) => theme.borderRadius.small};
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  .collapsible {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-end;
    > div {
      margin-bottom: ${({ theme }) => theme.spacing(3)};
    }
    position: relative;
    ${({ theme }) => theme.media.showAt({ at: 1 }) `
      > div {
        margin-bottom: ${theme.spacing(2)};
      }
    `}
  }
`;

export const UserRatingContainer = styled.div`
  display: flex;
  align-items: center;
  width: 45%;
  svg {
    margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ theme }) => theme.spacing(2)};
    path {
      fill: ${({ theme }) => theme.colors.neutrals.grey1};
    }
  }
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    width: 100%;
    svg {
      margin-${theme.isRTL ? 'left' : 'right'}: ${theme.spacing(1)};
    }
  `}
`;

export const UserRatingBarContainer = styled.div`
  width: 92px;
  margin-${({ theme }) => theme.isRTL ? 'right' : 'left'}: auto;
  margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ theme }) => theme.spacing(2)};
  height: 6px;
  background: ${({ theme }) => theme.colors.neutrals.grey8};
  border-radius: ${({ theme }) => theme.borderRadius.smallestSideRound};
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    width: 76px;
  `}
`;

export const UserRating = styled.div<{ ratingPercentage: number }>`
  width: ${({ ratingPercentage }) => ratingPercentage}%;
  background: ${({ theme }) => theme.colors.brand.main};
  height: 6px;
  border-radius: ${({ theme }) => theme.borderRadius.smallestSideRound};
`;

export const RealtorsRow = styled.div`
  display: flex;
  ${({ theme }) => theme.media.showAt({ to: 2 }) `
    flex-wrap: wrap;
  `}
`;

export const RealtorRowItem = styled.a`
  text-decoration: none;
  color: unset;
  ${({ theme }) => theme.media.showAt({ to: 2 }) `
    flex-basis: 100%;
    &:not(:last-of-type) {
      margin-bottom: ${theme.spacing(2)};
    }
  `}
  ${({ theme }) => theme.media.showAt({ from: 3 }) `
    flex: 1;
    max-width: 50%;
    &:not(:last-of-type) {
      margin-${theme.isRTL ? 'left' : 'right'}: ${theme.spacing(2)};
    }
  `}
  > div {
    height: 100%;
  }
`;

export const LocalWinnersSection = styled(BaseLocalWinnersSection)`
  ${({ theme }) => `
    margin-top: ${theme.spacing(8)};
  `}
`;

export const BlogSectionBg = styled.div`
  ${({ theme }) => `
    background: ${theme.colors.neutrals.grey10};
    margin: ${theme.spacing(5)} 0;
  `}
`;

export const BlogWrap = styled(WithContentSlotSidePaddingContainer)`
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    padding: 0;
    div.carousel-shadow-root div.view-pager-rail,
    .area__blog-header {
      padding: 0 ${theme.spacing(3)};
    }
  `}
`;
